import React, { useState, useEffect } from 'react';
import { computeCognitiveProfile, buildFullProfile } from '../utils/cognitiveProfile';

// ── Inject keyframes once ─────────────────────────────────────────────────────
let _kfDone = false;
function injectKF() {
  if (_kfDone || document.getElementById('cog-ob-kf')) return;
  _kfDone = true;
  const s = document.createElement('style');
  s.id = 'cog-ob-kf';
  s.textContent = `
    @keyframes cogFadeUp  { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
    @keyframes cogFadeIn  { from{opacity:0} to{opacity:1} }
    @keyframes cogSlideR  { from{opacity:0;transform:translateX(22px)} to{opacity:1;transform:translateX(0)} }
  `;
  document.head.appendChild(s);
}

// ── Questions definition ──────────────────────────────────────────────────────
const QUESTIONS = [
  {
    text: 'Você precisa aprender algo novo para amanhã.',
    note: 'Qual abordagem faz mais sentido?',
    options: [
      { l: 'A', t: 'Ver alguém fazendo primeiro e depois repetir' },
      { l: 'B', t: 'Ler uma explicação organizada passo a passo' },
      { l: 'C', t: 'Ver imagens, diagramas ou esquemas visuais' },
      { l: 'D', t: 'Conversar sobre o assunto enquanto aprende' },
    ],
  },
  {
    text: 'Você está tentando terminar uma tarefa importante.',
    note: 'O que normalmente faz você perder o ritmo primeiro?',
    options: [
      { l: 'A', t: 'Muito texto seguido' },
      { l: 'B', t: 'Muitas informações ao mesmo tempo' },
      { l: 'C', t: 'Falta de direção clara' },
      { l: 'D', t: 'Sentir que não estou progredindo' },
    ],
  },
  {
    text: 'Você está travado em um problema difícil.',
    note: 'O que seria mais útil agora?',
    options: [
      { l: 'A', t: 'Um resumo rápido do essencial' },
      { l: 'B', t: 'Um passo a passo completo e claro' },
      { l: 'C', t: 'Um exemplo visual ou diagrama' },
      { l: 'D', t: 'Uma conversa guiada com a Athena' },
    ],
  },
  {
    text: 'Você vai passar duas horas focado em algo importante.',
    note: 'Qual ambiente parece mais confortável?',
    options: [
      { l: 'A', t: 'Minimalista e sem distrações' },
      { l: 'B', t: 'Organizado e com estrutura visível' },
      { l: 'C', t: 'Rico em referências e exemplos visuais' },
      { l: 'D', t: 'Dinâmico, onde posso interagir e perguntar' },
    ],
  },
  {
    text: 'Quando você sente que está aprendendo melhor?',
    note: null,
    options: [
      { l: 'A', t: 'Quando vejo claramente meu progresso' },
      { l: 'B', t: 'Quando consigo conectar as ideias entre si' },
      { l: 'C', t: 'Quando vejo exemplos reais sendo usados' },
      { l: 'D', t: 'Quando pratico imediatamente o que aprendi' },
    ],
  },
  {
    text: 'Escolha a situação que mais parece com você.',
    note: null,
    options: [
      { l: 'A', t: 'Se estiver organizado, consigo focar' },
      { l: 'B', t: 'Se entender o propósito, consigo focar' },
      { l: 'C', t: 'Se eu visualizar, consigo focar' },
      { l: 'D', t: 'Se eu interagir, consigo focar' },
    ],
  },
];

const TRAIT_COLORS = {
  VISUAL:         '#818cf8',
  ESTRUTURADO:    '#38bdf8',
  CONVERSACIONAL: '#34d399',
  PRÁTICO:        '#fb923c',
};

const TRAIT_LABELS = {
  VISUAL:         'Visual',
  ESTRUTURADO:    'Estruturado',
  CONVERSACIONAL: 'Conversacional',
  PRÁTICO:        'Prático',
};

// ── Sub-components ────────────────────────────────────────────────────────────

function IntroScreen({ name, onNext, isMobile }) {
  useEffect(() => { injectKF(); }, []);
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', flex: 1,
      padding: isMobile ? '0 28px' : '0 56px',
      textAlign: 'center', gap: 28,
      animation: 'cogFadeUp .45s ease',
    }}>
      {/* Athena pulse indicator */}
      <div style={{
        width: 54, height: 54, borderRadius: '50%',
        border: '1px solid rgba(56,189,248,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 0 28px rgba(56,189,248,0.12), inset 0 0 12px rgba(56,189,248,0.05)',
      }}>
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
          <circle cx="11" cy="11" r="3.5" fill="rgba(56,189,248,0.85)" />
          <circle cx="11" cy="11" r="6.5" stroke="rgba(56,189,248,0.28)" strokeWidth="1" fill="none" />
          {[0,90,180,270].map(deg => {
            const r = Math.PI * deg / 180;
            const x1 = 11 + Math.cos(r) * 8;
            const y1 = 11 + Math.sin(r) * 8;
            const x2 = 11 + Math.cos(r) * 10;
            const y2 = 11 + Math.sin(r) * 10;
            return <line key={deg} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(56,189,248,0.45)" strokeWidth="1.2" strokeLinecap="round"/>;
          })}
        </svg>
      </div>

      <div>
        <h2 style={{
          margin: '0 0 14px', fontSize: isMobile ? 20 : 23,
          fontWeight: 300, color: '#eef5ff', letterSpacing: '0.04em',
        }}>
          Olá, {name}
        </h2>
        <p style={{
          margin: 0, fontSize: isMobile ? 14 : 15, fontWeight: 400,
          color: 'rgba(148,163,184,0.72)', lineHeight: 1.75, maxWidth: 400,
        }}>
          Vou fazer algumas perguntas rápidas para entender como você aprende e se concentra melhor.
          <br /><br />
          Isso vai personalizar o modo Foco Total para o seu perfil.
        </p>
      </div>

      <button
        onClick={onNext}
        style={{
          padding: isMobile ? '12px 36px' : '13px 44px',
          fontSize: 14, letterSpacing: '0.05em',
          background: 'rgba(56,189,248,0.1)',
          border: '1px solid rgba(56,189,248,0.38)',
          color: '#38bdf8', borderRadius: 12,
          cursor: 'pointer', fontFamily: 'inherit',
          transition: 'all .22s ease',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = 'rgba(56,189,248,0.18)';
          e.currentTarget.style.boxShadow = '0 0 24px rgba(56,189,248,0.14)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'rgba(56,189,248,0.1)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        Começar →
      </button>
    </div>
  );
}

function AnswerOption({ letter, text, selected, onSelect, isMobile }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onSelect}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: isMobile ? '13px 14px' : '14px 18px',
        borderRadius: 13,
        background: selected
          ? 'rgba(56,189,248,0.09)'
          : hov ? 'rgba(56,189,248,0.04)' : 'rgba(8,18,50,0.55)',
        border: selected
          ? '1px solid rgba(56,189,248,0.6)'
          : hov ? '1px solid rgba(56,189,248,0.22)' : '1px solid rgba(56,189,248,0.11)',
        boxShadow: selected ? '0 0 18px rgba(56,189,248,0.08)' : 'none',
        backdropFilter: 'blur(6px)',
        cursor: 'pointer', transition: 'all .18s ease',
        userSelect: 'none', WebkitUserSelect: 'none',
      }}
    >
      <div style={{
        width: isMobile ? 28 : 31, height: isMobile ? 28 : 31, borderRadius: '50%',
        background: selected ? 'rgba(56,189,248,0.2)' : 'rgba(56,189,248,0.05)',
        border: selected ? '1px solid rgba(56,189,248,0.55)' : '1px solid rgba(56,189,248,0.18)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        transition: 'all .18s ease',
      }}>
        <span style={{
          fontSize: 11.5, fontWeight: 600,
          color: selected ? '#38bdf8' : 'rgba(148,163,184,0.55)',
          lineHeight: 1,
        }}>
          {selected ? '✓' : letter}
        </span>
      </div>
      <span style={{
        fontSize: isMobile ? 13 : 13.5, fontWeight: 400, lineHeight: 1.45,
        color: selected ? '#eef5ff' : 'rgba(172,196,236,0.7)',
        flex: 1, transition: 'color .18s ease',
      }}>
        {text}
      </span>
    </div>
  );
}

function QuestionScreen({ question, stepNum, totalSteps, selected, onSelect, onNext, onPrev, isMobile }) {
  useEffect(() => { injectKF(); }, []);
  const canNext = selected !== null;

  return (
    <div
      key={stepNum}
      style={{
        display: 'flex', flexDirection: 'column',
        flex: 1, padding: isMobile ? '0 20px' : '0 52px',
        animation: 'cogSlideR .32s ease',
      }}
    >
      {/* Progress bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isMobile ? 22 : 28 }}>
        <div style={{ flex: 1, height: 2, background: 'rgba(56,189,248,0.09)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${(stepNum / totalSteps) * 100}%`,
            background: 'linear-gradient(90deg, #38bdf8, #818cf8)',
            borderRadius: 2, transition: 'width .5s cubic-bezier(.22,1,.36,1)',
          }} />
        </div>
        <span style={{ fontSize: 11, color: 'rgba(100,116,139,0.55)', letterSpacing: '0.1em', flexShrink: 0 }}>
          {stepNum} / {totalSteps}
        </span>
      </div>

      {/* Question */}
      <div style={{ marginBottom: isMobile ? 18 : 22 }}>
        <p style={{
          margin: '0 0 6px', fontSize: isMobile ? 16 : 18, fontWeight: 300,
          color: '#eef5ff', lineHeight: 1.4, letterSpacing: '0.015em',
        }}>
          {question.text}
        </p>
        {question.note && (
          <p style={{
            margin: 0, fontSize: isMobile ? 13 : 13.5,
            color: 'rgba(148,163,184,0.55)', lineHeight: 1.4,
          }}>
            {question.note}
          </p>
        )}
      </div>

      {/* Options */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 9 : 11, flex: 1 }}>
        {question.options.map(opt => (
          <AnswerOption
            key={opt.l}
            letter={opt.l}
            text={opt.t}
            selected={selected === opt.l}
            onSelect={() => onSelect(opt.l)}
            isMobile={isMobile}
          />
        ))}
      </div>

      {/* Navigation */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginTop: isMobile ? 18 : 22,
      }}>
        <button
          onClick={onPrev}
          style={{
            padding: '8px 16px', fontSize: 12.5,
            background: 'none', border: '1px solid rgba(255,255,255,0.07)',
            color: 'rgba(100,116,139,0.6)', borderRadius: 9,
            cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.02em',
          }}
        >
          ← Anterior
        </button>
        <button
          onClick={onNext}
          disabled={!canNext}
          style={{
            padding: isMobile ? '10px 24px' : '10px 30px', fontSize: 13.5,
            background: canNext ? 'rgba(56,189,248,0.12)' : 'rgba(56,189,248,0.03)',
            border: canNext ? '1px solid rgba(56,189,248,0.38)' : '1px solid rgba(56,189,248,0.1)',
            color: canNext ? '#38bdf8' : 'rgba(56,189,248,0.25)',
            borderRadius: 9, cursor: canNext ? 'pointer' : 'default',
            fontFamily: 'inherit', letterSpacing: '0.04em',
            transition: 'all .2s ease',
          }}
          onMouseEnter={e => { if (canNext) e.currentTarget.style.background = 'rgba(56,189,248,0.2)'; }}
          onMouseLeave={e => { if (canNext) e.currentTarget.style.background = 'rgba(56,189,248,0.12)'; }}
        >
          {stepNum === totalSteps ? 'Ver resultado →' : 'Próxima →'}
        </button>
      </div>
    </div>
  );
}

function SummaryScreen({ name, summary, traits, onConfirm, onRedo, isMobile }) {
  useEffect(() => { injectKF(); }, []);
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      flex: 1, padding: isMobile ? '0 20px' : '0 52px',
      overflowY: 'auto', animation: 'cogFadeUp .42s ease',
    }}>
      <div style={{ width: '100%', maxWidth: 460 }}>

        {/* Header */}
        <div style={{
          textAlign: 'center', marginBottom: isMobile ? 16 : 20,
          paddingBottom: isMobile ? 14 : 16,
          borderBottom: '1px solid rgba(56,189,248,0.09)',
        }}>
          <div style={{
            fontSize: isMobile ? 10 : 11, letterSpacing: '0.14em',
            textTransform: 'uppercase', color: 'rgba(56,189,248,0.65)',
            marginBottom: 6,
          }}>
            Perfil Cognitivo Identificado
          </div>
          <h2 style={{
            margin: '0 0 10px', fontSize: isMobile ? 19 : 22,
            fontWeight: 300, color: '#eef5ff', letterSpacing: '0.04em',
          }}>
            {name}
          </h2>
          {/* Trait badges */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            {traits.slice(0, 2).map(t => (
              <span key={t} style={{
                fontSize: 11, padding: '3px 11px', borderRadius: 20,
                background: `${TRAIT_COLORS[t]}15`,
                border: `1px solid ${TRAIT_COLORS[t]}38`,
                color: TRAIT_COLORS[t], letterSpacing: '0.06em',
              }}>
                {TRAIT_LABELS[t]}
              </span>
            ))}
          </div>
        </div>

        {/* Summary bullets */}
        <div style={{
          display: 'flex', flexDirection: 'column',
          gap: isMobile ? 9 : 10, marginBottom: isMobile ? 16 : 18,
        }}>
          {summary.map((line, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              padding: isMobile ? '10px 13px' : '11px 15px',
              background: 'rgba(8,18,50,0.55)',
              border: '1px solid rgba(56,189,248,0.1)',
              borderRadius: 12, backdropFilter: 'blur(6px)',
            }}>
              <div style={{
                width: 4, height: 4, borderRadius: '50%', marginTop: 6,
                background: 'rgba(56,189,248,0.65)', flexShrink: 0,
              }} />
              <span style={{
                fontSize: isMobile ? 13 : 13.5,
                color: 'rgba(172,196,236,0.8)', lineHeight: 1.55,
              }}>
                {line}
              </span>
            </div>
          ))}
        </div>

        {/* Adaptive note */}
        <p style={{
          margin: '0 0 20px', fontSize: isMobile ? 11 : 12,
          color: 'rgba(100,116,139,0.55)', textAlign: 'center', lineHeight: 1.55,
        }}>
          Este perfil poderá ser ajustado automaticamente conforme você usa o modo Foco Total.
        </p>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onRedo} style={{
            padding: '10px 18px', fontSize: 13, flexShrink: 0,
            background: 'none', border: '1px solid rgba(255,255,255,0.07)',
            color: 'rgba(100,116,139,0.65)', borderRadius: 9,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>
            Refazer
          </button>
          <button
            onClick={onConfirm}
            style={{
              flex: 1, padding: isMobile ? '11px 0' : '12px 0', fontSize: 14,
              background: 'rgba(56,189,248,0.12)',
              border: '1px solid rgba(56,189,248,0.38)',
              color: '#38bdf8', borderRadius: 9,
              cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.04em',
              transition: 'all .22s ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(56,189,248,0.2)';
              e.currentTarget.style.boxShadow = '0 0 22px rgba(56,189,248,0.13)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'rgba(56,189,248,0.12)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Confirmar e começar →
          </button>
        </div>

      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export function CognitiveOnboarding({ profileBase, onComplete, onBack, isMobile }) {
  const [step,      setStep]    = useState(0); // 0=intro, 1-6=questions, 7=summary
  const [answers,   setAnswers] = useState(Array(6).fill(null));
  const [cogResult, setCogResult] = useState(null);

  const TOTAL = QUESTIONS.length; // 6

  const handleSelect = (letter) => {
    const qi = step - 1;
    setAnswers(prev => { const n = [...prev]; n[qi] = letter; return n; });
  };

  const handleNext = () => {
    if (step === 0) { setStep(1); return; }
    if (step < TOTAL) { setStep(s => s + 1); return; }
    if (step === TOTAL) {
      const result = computeCognitiveProfile(answers);
      setCogResult(result);
      setStep(7);
    }
  };

  const handlePrev = () => {
    if (step === 0 || step === 1) { setStep(0); return; }
    setStep(s => s - 1);
  };

  const handleConfirm = () => {
    const full = buildFullProfile(profileBase, cogResult, answers);
    onComplete(full);
  };

  const handleRedo = () => {
    setStep(0);
    setAnswers(Array(6).fill(null));
    setCogResult(null);
  };

  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 10,
      background: 'radial-gradient(ellipse 100% 55% at 50% -5%, rgba(14,40,120,0.22) 0%, rgba(3,9,23,0.98) 60%, rgba(2,5,14,1) 100%)',
      display: 'flex', flexDirection: 'column',
      fontFamily: '-apple-system,BlinkMacSystemFont,"Inter","Segoe UI",sans-serif',
    }}>

      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: isMobile ? '14px 18px' : '18px 48px',
        borderBottom: '1px solid rgba(56,189,248,0.06)',
        flexShrink: 0,
      }}>
        <button
          onClick={onBack}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '6px 11px', fontSize: 12,
            background: 'none', border: '1px solid rgba(255,255,255,0.07)',
            color: 'rgba(100,116,139,0.6)', borderRadius: 8,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M 7 1.5 L 2.5 5 L 7 8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Voltar
        </button>

        <span style={{
          fontSize: isMobile ? 10 : 11, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: 'rgba(56,189,248,0.35)',
        }}>
          Perfil Cognitivo
        </span>

        {/* right spacer — matches back button width */}
        <div style={{ width: 68 }} />
      </div>

      {/* Content area */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        padding: isMobile ? '22px 0 16px' : '34px 0 20px',
        overflowY: 'auto',
      }}>
        {step === 0 && (
          <IntroScreen name={profileBase.name} onNext={handleNext} isMobile={isMobile} />
        )}
        {step >= 1 && step <= 6 && (
          <QuestionScreen
            key={step}
            question={QUESTIONS[step - 1]}
            stepNum={step}
            totalSteps={TOTAL}
            selected={answers[step - 1]}
            onSelect={handleSelect}
            onNext={handleNext}
            onPrev={handlePrev}
            isMobile={isMobile}
          />
        )}
        {step === 7 && cogResult && (
          <SummaryScreen
            name={profileBase.name}
            summary={cogResult.summary}
            traits={cogResult.traits}
            onConfirm={handleConfirm}
            onRedo={handleRedo}
            isMobile={isMobile}
          />
        )}
      </div>

    </div>
  );
}
